import { ObjectValues } from '@beyonnex/bx-angular-ui';

export const PRODUCT = {
  HKA: 'HKA',
  UVI: 'UVI',
  BKA: 'BKA',
  RWM: 'RWM',
  HEAT_CONTROL: 'HEAT_CONTROL',
} as const;

export const PROPERTY_STATUS = {
  CRITICAL: 'CRITICAL',
  PENDING: 'PENDING',
  OK: 'OK',
} as const;

/**
 * This service's own severity scale, deliberately kept apart from `CARDINALITY`: both are small
 * integers and they disagree — 20 is CRITICAL here but OK there. Never map one onto the other.
 */
export const PRODUCT_SEVERITY = {
  CRITICAL: 20,
  PENDING: 10,
  OK: 0,
} as const;

export type Product = ObjectValues<typeof PRODUCT>;
export type PropertyStatusName = ObjectValues<typeof PROPERTY_STATUS>;
export type ProductSeverity = ObjectValues<typeof PRODUCT_SEVERITY>;

export const PRODUCT_SEVERITY_VALUES: ProductSeverity[] = Object.values(PRODUCT_SEVERITY);

export function isProductSeverity(severity?: number): severity is ProductSeverity {
  if (!severity && severity !== 0) {
    return false;
  }

  return PRODUCT_SEVERITY_VALUES.includes(severity as ProductSeverity);
}

/** The property level sends only `overallStatus` — no numeric severity — so its name resolves to one. */
export const PROPERTY_STATUS_SEVERITY: Record<PropertyStatusName, ProductSeverity> = {
  [PROPERTY_STATUS.CRITICAL]: PRODUCT_SEVERITY.CRITICAL,
  [PROPERTY_STATUS.PENDING]: PRODUCT_SEVERITY.PENDING,
  [PROPERTY_STATUS.OK]: PRODUCT_SEVERITY.OK,
};

/** Shared by the property-wide totals and each product's own breakdown. */
export interface StatusCounts {
  devices: number;
  devicesInError: number;
  devicesInPending: number;
  roomGroups: number;
  roomGroupsImpacted: number;
}

export interface ProductStatus extends StatusCounts {
  product: Product;
  status: PropertyStatusName;
  severity: ProductSeverity;
  reason: string;
  changedAt: string;
}

/** One billing unit's status, as returned both by the single-property endpoint and as a list item. */
export interface PropertyStatus {
  billingUnitNumber: string;
  /** The Ditto thing id of the property, which is how the property details route identifies it. */
  thingId: string;
  overallStatus: PropertyStatusName;
  score: number;
  /** Error and pending combined. Ordering uses `errorRate`, not this. */
  rate: number;
  errorRate: number;
  totals: StatusCounts;
  /** Only the products this billing unit serves; an absent product is not served. */
  products: ProductStatus[];
  computedAt: string;
  overallChangedAt: string;
}

export interface PropertyStatusListResponse {
  items: PropertyStatus[];
  page: number;
  size: number;
  total: number;
}

export interface PropertyStatusQuery {
  billingUnit?: string;
  product?: Product[];
  page?: number;
  size?: number;
}
